import React, { useCallback, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  Divider,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AddModeratorOutlined,
  AdminPanelSettingsOutlined,
  Delete as DeleteIcon,
  GroupOutlined,
  LockResetOutlined,
  PersonAddAlt1Outlined,
  ShieldOutlined,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { isAdminUser, useUser } from "./UserContext";
import { deleteUser, listRoles, listUsers, setUserPassword, setUserRoles } from "../api/agentApi";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { PERMISSIONS } from "../permissions";

const copper = BRAND_COLORS.primary.main;
const copperDark = BRAND_COLORS.primary.dark;
const softCopper = BRAND_COLORS.primary.soft;
const green = "#437848";
const softGreen = "rgba(67,120,72,0.12)";
const borderSoft = "#E5D4C4";

const formControlSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
  },
};

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 44,
  px: 2.4,
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

const secondaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  borderColor: borderSoft,
  color: copperDark,
  textTransform: "none",
  fontWeight: 850,
  minHeight: 40,
  px: 2,
  "&:hover": {
    borderColor: copper,
    bgcolor: BRAND_COLORS.primary.softSubtle,
  },
};

// Password validation constants
const specialChars = "!@#$%^&*";
const passwordHint =
  `Password must be at least 8 characters and contain at least 1 uppercase letter, 1 numeral, and 1 special character (${specialChars}).`;
function validatePassword(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*]/.test(password)
  );
}

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  created_at: string;
  password_set_by_operator: boolean;
  is_service_account?: boolean;
  roles?: RoleSummary[];
}

interface RoleSummary {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_builtin?: boolean;
  is_system?: boolean;
}

interface DeleteDialogState {
  open: boolean;
  user: User | null;
}

interface ResetPasswordDialogState {
  open: boolean;
  user: User | null;
  newPassword: string;
  confirmPassword: string;
  passwordValid: boolean;
  passwordsMatch: boolean;
}

interface RoleAssignmentDialogState {
  open: boolean;
  user: User | null;
  roleIds: string[];
}

const UserListPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const copperText = isDark ? "#F2C69B" : copperDark;
  const softCopperSurface = isDark ? "rgba(224,177,132,0.14)" : softCopper;
  const softGreenSurface = isDark ? "rgba(91,160,92,0.18)" : softGreen;
  const greenText = isDark ? "#CFEFD4" : green;
  const cardShadow = isDark ? "0 18px 54px rgba(0,0,0,0.34)" : "0 16px 44px rgba(20,18,15,0.06)";
  const panelBorder = isDark ? "rgba(224,177,132,0.24)" : borderSoft;
  const headerCellSx = {
    bgcolor: isDark ? "rgba(224,177,132,0.10)" : "#F7F2ED",
    fontWeight: 900,
    color: "text.secondary",
  };
  const themedSecondaryActionButtonSx = {
    ...secondaryActionButtonSx,
    borderColor: isDark ? "rgba(224,177,132,0.34)" : borderSoft,
    color: copperText,
    "&:hover": {
      borderColor: isDark ? "rgba(224,177,132,0.52)" : copper,
      bgcolor: isDark ? "rgba(224,177,132,0.12)" : BRAND_COLORS.primary.softSubtle,
    },
  };
  const { user: currentUser, can } = useUser();
  const canReadUsers = Boolean(can?.(PERMISSIONS.USERS_READ));
  const canManageUsers = Boolean(can?.(PERMISSIONS.USERS_MANAGE));
  const canViewSystemRoles = isAdminUser(currentUser);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    user: null,
  });

  // Reset password dialog state
  const [resetPasswordDialog, setResetPasswordDialog] = useState<ResetPasswordDialogState>({
    open: false,
    user: null,
    newPassword: "",
    confirmPassword: "",
    passwordValid: true,
    passwordsMatch: true,
  });
  const [roleDialog, setRoleDialog] = useState<RoleAssignmentDialogState>({
    open: false,
    user: null,
    roleIds: [],
  });

  const refreshUsers = useCallback(async () => {
    const response = await listUsers(offset, limit);
    setUsers(response.users || []);
    setTotal(response.total || 0);
  }, [offset, limit]);

  // Fetch users
  useEffect(() => {
    if (!canReadUsers) return;

    const fetchUsers = async () => {
      setLoading(true);
      setError("");
      try {
        await refreshUsers();
      } catch (err: any) {
        setError(err?.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [canReadUsers, refreshUsers]);

  useEffect(() => {
    if (!canManageUsers) return;
    let active = true;

    const fetchRoles = async () => {
      setRolesLoading(true);
      try {
        const response = await listRoles();
        if (active) setRoles((response.roles || []).filter((item: RoleSummary) => canViewSystemRoles || !item.is_system));
      } catch {
        if (active) setRoles([]);
      } finally {
        if (active) setRolesLoading(false);
      }
    };

    fetchRoles();
    return () => {
      active = false;
    };
  }, [canManageUsers, canViewSystemRoles]);

  const adminCount = useMemo(
    () => users.filter((item) => item.role === "admin" || item.role === "operator-admin").length,
    [users],
  );
  const resetRequiredCount = useMemo(
    () => users.filter((item) => item.password_set_by_operator).length,
    [users],
  );

  // Access control
  if (!currentUser || !canReadUsers) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Alert severity="error">
          Access denied. You do not have permission to list users.
        </Alert>
      </Box>
    );
  }

  // Handle delete user
  const handleDeleteClick = (user: User) => {
    setDeleteDialog({ open: true, user });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return;

    try {
      await deleteUser(deleteDialog.user.id);
      setSuccessMessage(`User "${deleteDialog.user.username}" deleted successfully`);
      setDeleteDialog({ open: false, user: null });

      // Refresh user list
      await refreshUsers();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      // Handle both string messages and API error objects
      let errorMessage = "Failed to delete user";

      const rawMessage =
        err && typeof err.message === "string" ? err.message : undefined;

      if (rawMessage) {
        try {
          // Try to parse JSON error response thrown by apiCall (res.text())
          const parsed = JSON.parse(rawMessage);
          const detail = (parsed as any).detail ?? (parsed as any).message;

          if (typeof detail === "string") {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            errorMessage = detail.join(", ");
          } else if (detail && typeof detail === "object") {
            errorMessage =
              typeof (detail as any).message === "string"
                ? (detail as any).message
                : JSON.stringify(detail);
          } else {
            errorMessage = rawMessage;
          }
        } catch {
          // Not JSON: use the raw message text
          errorMessage = rawMessage;
        }
      } else if (err && typeof err.detail === "string") {
        errorMessage = err.detail;
      } else if (typeof err === "string") {
        errorMessage = err;
      }
      setError(errorMessage);
      // Close dialog; error message is displayed separately for the user to see
      setDeleteDialog({ open: false, user: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, user: null });
  };

  // Handle reset password
  const handleResetPasswordClick = (user: User) => {
    setResetPasswordDialog({
      open: true,
      user,
      newPassword: "",
      confirmPassword: "",
      passwordValid: true,
      passwordsMatch: true,
    });
  };

  const handlePasswordChange = (password: string) => {
    setResetPasswordDialog((prev) => ({
      ...prev,
      newPassword: password,
      passwordValid: validatePassword(password),
      passwordsMatch: password === prev.confirmPassword,
    }));
  };

  const handleConfirmPasswordChange = (password: string) => {
    setResetPasswordDialog((prev) => ({
      ...prev,
      confirmPassword: password,
      passwordsMatch: prev.newPassword === password,
    }));
  };

  const handleResetPasswordConfirm = async () => {
    if (!resetPasswordDialog.user) return;

    if (!resetPasswordDialog.passwordsMatch) {
      setError("Passwords must match");
      return;
    }

    if (!validatePassword(resetPasswordDialog.newPassword)) {
      setError("Password does not meet requirements");
      return;
    }

    try {
      await setUserPassword(resetPasswordDialog.user.id, resetPasswordDialog.newPassword);
      setSuccessMessage(
        `Password reset successfully for user "${resetPasswordDialog.user.username}"`
      );
      setResetPasswordDialog({
        open: false,
        user: null,
        newPassword: "",
        confirmPassword: "",
        passwordValid: true,
        passwordsMatch: true,
      });

      // Refresh user list to update password_set_by_operator status
      await refreshUsers();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err?.message || "Failed to reset password");
      setResetPasswordDialog({
        open: false,
        user: null,
        newPassword: "",
        confirmPassword: "",
        passwordValid: true,
        passwordsMatch: true,
      });
    }
  };

  const handleResetPasswordCancel = () => {
    setResetPasswordDialog({
      open: false,
      user: null,
      newPassword: "",
      confirmPassword: "",
      passwordValid: true,
      passwordsMatch: true,
    });
  };

  const handleManageRolesClick = (selectedUser: User) => {
    setRoleDialog({
      open: true,
      user: selectedUser,
      roleIds: (selectedUser.roles || []).map((item) => item.id),
    });
  };

  const handleRoleToggle = (roleId: string) => {
    const role = roles.find((item) => item.id === roleId);
    if (role?.is_system && !roleDialog.user?.is_service_account) return;
    setRoleDialog((current) => ({
      ...current,
      roleIds: current.roleIds.includes(roleId)
        ? current.roleIds.filter((id) => id !== roleId)
        : [...current.roleIds, roleId],
    }));
  };

  const handleRoleAssignmentCancel = () => {
    setRoleDialog({ open: false, user: null, roleIds: [] });
  };

  const handleRoleAssignmentSave = async () => {
    if (!roleDialog.user) return;
    try {
      await setUserRoles(roleDialog.user.id, roleDialog.roleIds);
      setSuccessMessage(`Roles updated for user "${roleDialog.user.username}"`);
      handleRoleAssignmentCancel();
      await refreshUsers();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err?.message || "Failed to update user roles");
    }
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit) || 1;

  const roleLabel = (role: string) => {
    if (role === "operator-admin") return "Operator Admin";
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const roleChipSx = (role: string) => {
    if (role === "operator-admin") {
      return {
        bgcolor: isDark ? "rgba(224,177,132,0.16)" : "rgba(147,92,41,0.14)",
        color: copperText,
        border: `1px solid ${isDark ? "rgba(224,177,132,0.30)" : "rgba(147,92,41,0.28)"}`,
      };
    }
    if (role === "admin") {
      return {
        bgcolor: softCopperSurface,
        color: copperText,
        border: `1px solid ${isDark ? "rgba(224,177,132,0.28)" : "rgba(184,115,51,0.25)"}`,
      };
    }
    if (role === "developer") {
      return {
        bgcolor: isDark ? alpha("#4C9AD6", 0.18) : "rgba(47,105,162,0.11)",
        color: isDark ? "#CDE6FF" : "#2F69A2",
        border: `1px solid ${isDark ? "rgba(140,190,238,0.30)" : "rgba(47,105,162,0.24)"}`,
      };
    }
    return {
      bgcolor: softGreenSurface,
      color: greenText,
      border: `1px solid ${isDark ? "rgba(145,205,153,0.30)" : "rgba(67,120,72,0.24)"}`,
    };
  };

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto", color: "text.primary" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Users
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35 }}>
            Manage account access, roles, and password recovery.
          </Typography>
        </Box>
        {canManageUsers && (
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
            <Button
              component={Link}
              to="/add-user"
              variant="contained"
              startIcon={<PersonAddAlt1Outlined />}
              sx={primaryActionButtonSx}
            >
              Add User
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={PAGE_SHELL.sectionGap} sx={{ mb: PAGE_SHELL.sectionGap }}>
        {[
          { label: "Total users", value: total || users.length, icon: <GroupOutlined />, tone: "copper" },
          { label: "Admins", value: adminCount, icon: <AdminPanelSettingsOutlined />, tone: "copper" },
          { label: "Password actions", value: resetRequiredCount, icon: <LockResetOutlined />, tone: "green" },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: UI_RADIUS.card,
              border: `1px solid ${panelBorder}`,
              bgcolor: "background.paper",
              boxShadow: cardShadow,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: item.tone === "green" ? softGreenSurface : softCopperSurface,
                  color: item.tone === "green" ? greenText : copperText,
                  borderRadius: UI_RADIUS.panel,
                }}
              >
                {item.icon}
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={950}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: PAGE_SHELL.sectionGap,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${panelBorder}`,
          bgcolor: "background.paper",
          boxShadow: isDark ? "0 18px 54px rgba(0,0,0,0.40)" : "0 18px 54px rgba(20,18,15,0.07)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CircularProgress sx={{ color: copper }} />
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Loading users...
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>User</TableCell>
                <TableCell sx={headerCellSx}>Email</TableCell>
                <TableCell sx={headerCellSx}>Role</TableCell>
                <TableCell sx={headerCellSx}>Created</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ py: 5 }}>
                      <ShieldOutlined sx={{ color: "text.disabled", fontSize: 38, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No users found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: isDark ? "rgba(224,177,132,0.08)" : "rgba(184,115,51,0.04)" },
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: softCopperSurface,
                            color: copperText,
                            fontWeight: 900,
                            fontSize: 14,
                          }}
                        >
                          {(user.username || "?").slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={850}>{user.username}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID {user.id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: "text.secondary" }}>{user.email}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {(user.roles || []).length > 0 ? (
                          (user.roles || []).map((roleItem) => (
                            <Chip
                              key={roleItem.id}
                              label={roleItem.name}
                              size="small"
                              sx={{
                                ...roleChipSx(user.role),
                                fontWeight: 800,
                                borderRadius: UI_RADIUS.chip,
                                height: 24,
                                px: 1.5,
                              }}
                            />
                          ))
                        ) : (
                          <Chip
                            label={roleLabel(user.role)}
                            size="small"
                            sx={{
                              ...roleChipSx(user.role),
                              fontWeight: 800,
                              borderRadius: UI_RADIUS.chip,
                              height: 24,
                              px: 1.5,
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: "text.secondary" }}>
                      <Tooltip title={user.created_at}>
                        <span>{new Date(user.created_at).toLocaleDateString()}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {user.password_set_by_operator ? (
                        <Chip
                          label="Reset required"
                          size="small"
                          sx={{
                            bgcolor: softCopperSurface,
                            color: copperText,
                            border: `1px solid ${isDark ? "rgba(224,177,132,0.28)" : BRAND_COLORS.primary.border}`,
                            fontWeight: 800,
                            borderRadius: UI_RADIUS.chip,
                            height: 24,
                            px: 1.5,
                          }}
                        />
                      ) : (
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            bgcolor: softGreenSurface,
                            color: greenText,
                            border: `1px solid ${isDark ? "rgba(145,205,153,0.30)" : "rgba(67,120,72,0.24)"}`,
                            fontWeight: 800,
                            borderRadius: UI_RADIUS.chip,
                            height: 24,
                            px: 1.5,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }} align="right">
                      {canManageUsers && (
                        <Tooltip title="Manage Roles">
                          <IconButton
                            size="small"
                            onClick={() => handleManageRolesClick(user)}
                            sx={{
                              color: copperText,
                              border: `1px solid ${isDark ? "rgba(224,177,132,0.28)" : BRAND_COLORS.primary.border}`,
                              borderRadius: UI_RADIUS.iconButton,
                              mr: 0.75,
                            }}
                          >
                            <AddModeratorOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageUsers && (
                        <Tooltip title="Reset Password">
                          <IconButton
                            size="small"
                            onClick={() => handleResetPasswordClick(user)}
                            sx={{
                              color: copperText,
                              border: `1px solid ${isDark ? "rgba(224,177,132,0.28)" : BRAND_COLORS.primary.border}`,
                              borderRadius: UI_RADIUS.iconButton,
                              mr: 0.75,
                            }}
                          >
                            <VpnKeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageUsers && (
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            sx={{
                              color: "#A6423B",
                              border: "1px solid rgba(166,66,59,0.22)",
                              borderRadius: UI_RADIUS.iconButton,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          disabled={offset === 0}
          onClick={handlePrevPage}
          variant="outlined"
          sx={themedSecondaryActionButtonSx}
        >
          Prev
        </Button>
        <Typography variant="body2" sx={{ mx: 2 }}>
          Page {currentPage} of {totalPages}
        </Typography>
        <Button
          disabled={offset + limit >= total}
          onClick={handleNextPage}
          variant="outlined"
          sx={themedSecondaryActionButtonSx}
        >
          Next
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user{" "}
            <strong>{deleteDialog.user?.username}</strong>? This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: "warning.main", fontWeight: 500 }}>
            Note: Users with associated keys cannot be deleted until the keys are deleted first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} sx={themedSecondaryActionButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ borderRadius: UI_RADIUS.button, fontWeight: 900, textTransform: "none" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Assignment Dialog */}
      <Dialog
        open={roleDialog.open}
        onClose={handleRoleAssignmentCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Manage roles</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Assign roles for user <strong>{roleDialog.user?.username}</strong>.
          </DialogContentText>
          {rolesLoading ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={0.5}>
              {roles.map((roleItem) => {
                const systemRoleDisabled = Boolean(roleItem.is_system && !roleDialog.user?.is_service_account);
                return (
                  <Stack
                    key={roleItem.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      border: `1px solid ${panelBorder}`,
                      borderRadius: UI_RADIUS.panel,
                      px: 1,
                      py: 0.75,
                      opacity: systemRoleDisabled ? 0.62 : 1,
                    }}
                  >
                    <Checkbox
                      checked={roleDialog.roleIds.includes(roleItem.id)}
                      disabled={systemRoleDisabled}
                      onChange={() => handleRoleToggle(roleItem.id)}
                    />
                    <ListItemText
                      primary={roleItem.name}
                      secondary={systemRoleDisabled ? "System role, assignable only to service accounts." : roleItem.description || roleItem.slug}
                    />
                  </Stack>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRoleAssignmentCancel} sx={themedSecondaryActionButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleRoleAssignmentSave}
            variant="contained"
            sx={primaryActionButtonSx}
            disabled={rolesLoading || roleDialog.roleIds.length === 0}
          >
            Save roles
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialog.open}
        onClose={handleResetPasswordCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Reset password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reset password for user{" "}
            <strong>{resetPasswordDialog.user?.username}</strong>
          </DialogContentText>
          <Divider sx={{ mb: 1 }} />
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={resetPasswordDialog.newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)}
            error={
              !resetPasswordDialog.passwordValid &&
              resetPasswordDialog.newPassword.length > 0
            }
            helperText={passwordHint}
            margin="normal"
            sx={formControlSx}
          />
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            value={resetPasswordDialog.confirmPassword}
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            error={
              !resetPasswordDialog.passwordsMatch &&
              resetPasswordDialog.confirmPassword.length > 0
            }
            helperText={
              !resetPasswordDialog.passwordsMatch &&
              resetPasswordDialog.confirmPassword.length > 0
                ? "Passwords must match"
                : ""
            }
            margin="normal"
            sx={formControlSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPasswordCancel} sx={themedSecondaryActionButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPasswordConfirm}
            color="primary"
            variant="contained"
            startIcon={<LockResetOutlined />}
            disabled={
              !resetPasswordDialog.newPassword ||
              !resetPasswordDialog.confirmPassword ||
              !resetPasswordDialog.passwordValid ||
              !resetPasswordDialog.passwordsMatch
            }
            sx={primaryActionButtonSx}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserListPage;
